import { FunctionComponent, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SidebarLayout } from '@/layouts/sidebar/SidebarLayout';
import { Permission } from '@/models/user';
import { PageWithLayout } from '@/models/page';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
    useCreateFilterTagMutation,
    useDeleteFilterTagMutation,
    useGetFilterTagsQuery,
    useReorderFilterTagsMutation,
    useUpdateFilterTagMutation,
} from '@/api/v2/filterTags';
import { useNotification } from '@starjack/features/notifications';
import { isConflict, saveErrorMessage, usageCount } from './tagErrors';
import { FilterTag } from '@/models/product';

const SortableRow: FunctionComponent<{
    tag: FilterTag;
    onEdit: () => void;
    onDelete: () => void;
}> = ({ tag, onEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: tag.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (
        <Card ref={setNodeRef} style={style} sx={{ p: 2 }}>
            <Stack direction='row' spacing={2} alignItems='center'>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{ cursor: 'grab', display: 'flex' }}
                >
                    <DragIndicatorIcon color='action' />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant='subtitle1'>{tag.nameDe}</Typography>
                    {tag.nameEn && (
                        <Typography variant='body2' color='text.secondary'>
                            EN: {tag.nameEn}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    aria-label='Bearbeiten'
                    onClick={onEdit}
                    size='small'
                >
                    <EditIcon />
                </IconButton>
                <IconButton
                    aria-label='Löschen'
                    onClick={onDelete}
                    size='small'
                    color='error'
                >
                    <DeleteIcon />
                </IconButton>
            </Stack>
        </Card>
    );
};

const FilterTagsPage: PageWithLayout = () => {
    // liftOperatorData is null until the stored session is rehydrated — destructuring it
    // straight away crashes the whole app on a direct hit or a reload of this route.
    const { liftOperatorData } = useAuthentication();
    const liftOperatorId = liftOperatorData?.liftOperatorId;
    const { addNotification } = useNotification();
    const { data: filterTags = [] } = useGetFilterTagsQuery(
        { liftOperatorId },
        { skip: !liftOperatorId }
    );
    const [createFilterTag] = useCreateFilterTagMutation();
    const [updateFilterTag] = useUpdateFilterTagMutation();
    const [deleteFilterTag] = useDeleteFilterTagMutation();
    const [reorderFilterTags] = useReorderFilterTagsMutation();

    const [order, setOrder] = useState<string[]>([]);
    // Reorder requests are chained and counted: overlapping drags would otherwise race, and the refetch each of
    // them triggers would push a stale server order back into the list while the user is still dragging.
    const reorderQueue = useRef<Promise<unknown>>(Promise.resolve());
    const reordersInFlight = useRef(0);

    useEffect(() => {
        if (reordersInFlight.current > 0) return;
        setOrder(filterTags.map((t) => t.id));
    }, [filterTags]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const [editing, setEditing] = useState<FilterTag | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState<FilterTag | null>(
        null
    );
    const [nameDe, setNameDe] = useState('');
    const [nameEn, setNameEn] = useState('');

    const orderedTags = order
        .map((id) => filterTags.find((t) => t.id === id))
        .filter((t): t is FilterTag => t != null);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = order.indexOf(String(active.id));
        const newIndex = order.indexOf(String(over.id));
        const newOrder = arrayMove(order, oldIndex, newIndex);
        setOrder(newOrder);

        reordersInFlight.current += 1;
        reorderQueue.current = reorderQueue.current
            .then(() =>
                reorderFilterTags({
                    liftOperatorId,
                    orderedIds: newOrder,
                }).unwrap()
            )
            .catch(() => {
                addNotification({
                    type: 'error',
                    message: 'Reihenfolge konnte nicht gespeichert werden',
                });
                // Drop back to the last order the server confirmed instead of leaving an unsaved one on screen.
                setOrder(filterTags.map((t) => t.id));
            })
            .finally(() => {
                reordersInFlight.current -= 1;
            });
    };

    const openEdit = (tag: FilterTag) => {
        setEditing(tag);
        setNameDe(tag.nameDe);
        setNameEn(tag.nameEn || '');
    };

    const handleSave = async () => {
        if (!nameDe.trim()) return;
        try {
            if (editing) {
                await updateFilterTag({
                    liftOperatorId,
                    filterTagId: editing.id,
                    payload: {
                        nameDe: nameDe.trim(),
                        nameEn: nameEn.trim() || null,
                    },
                }).unwrap();
            } else {
                await createFilterTag({
                    liftOperatorId,
                    payload: {
                        nameDe: nameDe.trim(),
                        nameEn: nameEn.trim() || null,
                    },
                }).unwrap();
            }
            setEditing(null);
            setCreateOpen(false);
            setNameDe('');
            setNameEn('');
        } catch (err) {
            addNotification({
                type: 'error',
                message: saveErrorMessage(err, 'einen Filtertag'),
            });
        }
    };

    const handleDelete = async (force: boolean) => {
        if (!deleteCandidate) return;
        try {
            await deleteFilterTag({
                liftOperatorId,
                filterTagId: deleteCandidate.id,
                force,
            }).unwrap();
            setDeleteCandidate(null);
        } catch (err) {
            // Offer the force delete on any conflict, not only when the payload carries a usage count.
            if (isConflict(err) && !force) {
                const count = usageCount(err);
                const inUse = count
                    ? `Dieser Filtertag ist in ${count} Produkten in Verwendung.`
                    : 'Dieser Filtertag ist noch in Verwendung.';
                if (confirm(`${inUse} Trotzdem löschen?`)) {
                    await handleDelete(true);
                }
            } else {
                addNotification({
                    type: 'error',
                    message: 'Löschen fehlgeschlagen',
                });
            }
        }
    };

    return (
        <Box sx={{ p: 4 }}>
            <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                mb={3}
            >
                <Typography variant='h4'>Filtertags verwalten</Typography>
                <Button
                    variant='contained'
                    onClick={() => {
                        setNameDe('');
                        setNameEn('');
                        setCreateOpen(true);
                    }}
                >
                    Neuer Filtertag
                </Button>
            </Stack>
            <Typography color='text.secondary' mb={3}>
                Reihenfolge per Drag and Drop. Wirkt sich direkt auf die Anzeige
                im Webshop aus.
            </Typography>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={order}
                    strategy={verticalListSortingStrategy}
                >
                    <Stack spacing={1}>
                        {orderedTags.map((tag) => (
                            <SortableRow
                                key={tag.id}
                                tag={tag}
                                onEdit={() => openEdit(tag)}
                                onDelete={() => setDeleteCandidate(tag)}
                            />
                        ))}
                    </Stack>
                </SortableContext>
            </DndContext>

            <Dialog
                open={editing != null || createOpen}
                onClose={() => {
                    setEditing(null);
                    setCreateOpen(false);
                }}
                fullWidth
                maxWidth='sm'
            >
                <DialogTitle>
                    {editing ? 'Filtertag bearbeiten' : 'Neuer Filtertag'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label='Bezeichnung Deutsch'
                            value={nameDe}
                            onChange={(e) => setNameDe(e.target.value)}
                            autoFocus
                            required
                        />
                        <TextField
                            label='Bezeichnung Englisch (optional)'
                            value={nameEn}
                            onChange={(e) => setNameEn(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setEditing(null);
                            setCreateOpen(false);
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleSave}
                        disabled={!nameDe.trim()}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteCandidate != null}
                onClose={() => setDeleteCandidate(null)}
            >
                <DialogTitle>Filtertag löschen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Soll der Filtertag „{deleteCandidate?.nameDe}“ gelöscht
                        werden? Falls er noch Produkten zugewiesen ist, folgt
                        eine Rückfrage, ob das Löschen erzwungen werden soll.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteCandidate(null)}>
                        Abbrechen
                    </Button>
                    <Button
                        color='error'
                        variant='contained'
                        onClick={() => handleDelete(false)}
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

FilterTagsPage.isProtected = true;
FilterTagsPage.getLayout = (page) => <SidebarLayout>{page}</SidebarLayout>;
FilterTagsPage.permissions = [Permission.LIFTOPERATOR_CONFIGURATION_NAV];

export default FilterTagsPage;
